// Google Analytics 4 / Google Tag Manager Event Utilities
// All tracking functions push to dataLayer for GTM processing

import { Tour } from "@/data/tours";
import { CartItem } from "@/contexts/CartContext";

// GTM Container ID - Replace with your actual GTM Container ID
export const GTM_ID = "GTM-XXXXXXX";

// GA4 Measurement ID (gtag.js)
export const GA_MEASUREMENT_ID = "G-MT59PDF807";

// Google Ads conversion tracking, leave empty until you have the real values.
// Fill BOTH to enable Google Ads conversion firing on WhatsApp click.
// GOOGLE_ADS_CONVERSION_ID, e.g. "AW-123456789"
// GOOGLE_ADS_CONVERSION_LABEL, e.g. "abcDEFghiJKL"
export const GOOGLE_ADS_CONVERSION_ID = "";
export const GOOGLE_ADS_CONVERSION_LABEL = "";

// Type declarations for dataLayer
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag: (...args: unknown[]) => void;
  }
}

// Initialize dataLayer if not present
const getDataLayer = (): Record<string, unknown>[] => {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }
  return [];
};

// Generic event push to dataLayer
const pushEvent = (eventName: string, eventParams: Record<string, unknown> = {}) => {
  const dataLayer = getDataLayer();
  dataLayer.push({
    event: eventName, ...eventParams,
  });
};

// Convert Tour to GA4 item format
const tourToItem = (tour: Tour, quantity = 1) => ({
  item_id: tour.id,
  item_name: tour.name,
  item_category: tour.island,
  item_category2: tour.category?.[0] || "",
  item_variant: tour.difficulty,
  price: parseFloat(tour.price.replace(/[^0-9.]/g, "")) || 0,
  quantity,
});

// ===== PAGE VIEW TRACKING =====

/**
 * Track page views - called on every route change
 */
export const trackPageView = (path: string, title?: string) => {
  pushEvent("page_view", {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

// ===== E-COMMERCE EVENTS =====

/**
 * Track when a tour detail page is viewed
 */
export const trackViewItem = (tour: Tour) => {
  pushEvent("view_item", {
    currency: "EUR",
    value: parseFloat(tour.price.replace(/[^0-9.]/g, "")) || 0,
    items: [tourToItem(tour)],
  });
};

/**
 * Track when a tour is added to cart
 */
export const trackAddToCart = (tour: Tour, quantity = 1) => {
  pushEvent("add_to_cart", {
    currency: "EUR",
    value: parseFloat(tour.price.replace(/[^0-9.]/g, "")) || quantity * (parseFloat(tour.price.replace(/[^0-9.]/g, "")) || 0),
    items: [tourToItem(tour, quantity)],
  });
};

/**
 * Track when a tour is removed from cart
 */
export const trackRemoveFromCart = (tour: Tour, quantity = 1) => {
  pushEvent("remove_from_cart", {
    currency: "EUR",
    value: parseFloat(tour.price.replace(/[^0-9.]/g, "")) || 0,
    items: [tourToItem(tour, quantity)],
  });
};

/**
 * Track when cart drawer is opened with items
 */
export const trackViewCart = (items: CartItem[]) => {
  const totalValue = items.reduce((sum, item) => {
    const price = parseFloat(item.tour.price.replace(/[^0-9.]/g, "")) || 0;
    return sum + price * item.participants;
  }, 0);

  pushEvent("view_cart", {
    currency: "EUR",
    value: totalValue,
    items: items.map((item) => tourToItem(item.tour, item.participants)),
  });
};

/**
 * Track when user clicks "Book via WhatsApp" - begin_checkout
 */
export const trackBeginCheckout = (items: CartItem[]) => {
  const totalValue = items.reduce((sum, item) => {
    const price = parseFloat(item.tour.price.replace(/[^0-9.]/g, "")) || 0;
    return sum + price * item.participants;
  }, 0);

  pushEvent("begin_checkout", {
    currency: "EUR",
    value: totalValue,
    items: items.map((item) => tourToItem(item.tour, item.participants)),
  });
};

// ===== LEAD GENERATION EVENTS =====

/**
 * Track WhatsApp click as lead generation
 */
export const trackWhatsAppClick = (context: string, tourName?: string) => {
  pushEvent("generate_lead", {
    currency: "EUR",
    lead_source: "whatsapp",
    lead_context: context,
    tour_name: tourName || "",
  });
};

/**
 * Track click on the "Send by Email" trigger button (modal opens).
 * Separate from WhatsApp lead events.
 */
export const trackEmailLeadClick = (source: string, language?: string) => {
  pushEvent("email_lead_click", {
    lead_source: "email",
    source,
    language: language || "",
  });
};

/**
 * Track successful submission of the "Send by Email" form.
 * Separate from WhatsApp generate_lead events.
 */
export const trackEmailLeadSubmit = (source: string, language?: string) => {
  pushEvent("email_lead_submit", {
    lead_source: "email",
    source,
    language: language || "",
  });
  // Also surface as generate_lead so GA4 conversion goals catch it.
  pushEvent("generate_lead", {
    lead_source: "email",
    lead_context: source,
  });
};


/**
 * Track booking inquiry submission
 */
export const trackBookingInquiry = (items: CartItem[], formData: Record<string, unknown>) => {
  const totalValue = items.reduce((sum, item) => {
    const price = parseFloat(item.tour.price.replace(/[^0-9.]/g, "")) || 0;
    return sum + price * item.participants;
  }, 0);

  pushEvent("generate_lead", {
    currency: "EUR",
    value: totalValue,
    lead_source: "booking_form",
    items_count: items.length,
    total_participants: items.reduce((sum, item) => sum + item.participants, 0),
    language: formData.language || "",
  });
};

// ===== ENGAGEMENT EVENTS =====

/**
 * Track search queries
 */
export const trackSearch = (query: string) => {
  pushEvent("search", {
    search_term: query,
  });
};

/**
 * Track contact form interaction
 */
export const trackContactFormStart = () => {
  pushEvent("contact_form_start", {});
};

/**
 * Track custom trip inquiry
 */
export const trackCustomTripInquiry = () => {
  pushEvent("custom_trip_inquiry", {
    lead_source: "custom_trip_form",
  });
};

/**
 * Track newsletter signup (if implemented)
 */
export const trackNewsletterSignup = (email: string) => {
  pushEvent("newsletter_signup", {
    method: "footer_form",
  });
};

// ===== UTILITY =====

/**
 * Check if analytics is enabled (GDPR consent gate).
 * Reads the same localStorage key used by CookieConsentContext, so it works
 * from anywhere (React or plain helpers) without coupling to the provider.
 */
export const isAnalyticsEnabled = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem("splora_cookie_consent") === "accepted";
  } catch {
    return false;
  }
};

/**
 * Centralized WhatsApp click handler.
 *
 * Always pushes the GA4 `generate_lead` event to dataLayer (trackWhatsAppClick).
 * Additionally fires a Google Ads `conversion` event via gtag IF:
 * - both GOOGLE_ADS_CONVERSION_ID and GOOGLE_ADS_CONVERSION_LABEL are set,
 * - the user has accepted cookies (isAnalyticsEnabled === true),
 * - window.gtag is available (loaded by AnalyticsProvider after consent).
 *
 * If the Ads constants are empty, nothing extra is sent, safe no-op.
 */
export const handleWhatsAppClick = (source: string, tourName?: string) => {
  // GA4 generate_lead, unchanged behaviour.
  trackWhatsAppClick(source, tourName);

  // Google Ads conversion, opt-in via constants + consent gate.
  if (!GOOGLE_ADS_CONVERSION_ID || !GOOGLE_ADS_CONVERSION_LABEL) return;
  if (!isAnalyticsEnabled()) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  try {
    window.gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`,
      event_category: "whatsapp",
      event_label: source,
      tour_name: tourName || "",
    });
  } catch {
    /* swallow, never break the click */
  }
};

